import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Field,
  Icon,
  Input,
  SectionTabs,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { useViewport } from '@/lib/viewport';
import { useScope } from '@/lib/scope';
import { useAuth } from '@/lib/auth';
import { useMockTable } from '@/hooks/useMockTable';
import { adjustmentService, isLate } from '@/services/adjustmentService';
import { CUTOFF_DAYS_BY_EVENT, type DayCalendarEntry, type EventType } from '@/domain';
import { toISODate } from '@/lib/utils';

type Method = 'matrix' | 'file' | 'api';

export function SubmitAdjustmentPage() {
  const { isMobile, vw } = useViewport();
  const { currentWarehouse, country } = useScope();
  const { user } = useAuth();
  const navigate = useNavigate();
  const customers = useMockTable('customers').filter((c) => c.is_active);
  const calendar = useMockTable('day_calendar');
  const [method, setMethod] = useState<Method>('matrix');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Lập kế hoạch', 'Gửi điều chỉnh']}
        title="Gửi điều chỉnh dự báo"
        subtitle={
          currentWarehouse
            ? `Tại ${currentWarehouse.code} · ${currentWarehouse.name} · Quốc gia ${country}`
            : 'Chọn kho ở thanh trên'
        }
        actions={
          <Button
            variant="outline"
            icon="history"
            onClick={() => navigate('/planner/forecast/review')}
          >
            Xem hàng đợi phê duyệt
          </Button>
        }
      />

      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        <CutoffStrip vw={vw} />

        <div style={{ marginTop: 16, marginBottom: 14 }}>
          <SectionTabs
            value={method}
            onChange={(s) => setMethod(s as Method)}
            tabs={[
              { id: 'matrix', label: 'Ma trận', idx: 1 },
              { id: 'file', label: 'Upload tệp', idx: 2 },
              { id: 'api', label: 'API endpoint', idx: 3 },
            ]}
          />
        </div>

        {method === 'matrix' && (
          <MatrixForm
            customers={customers}
            calendar={calendar}
            warehouseId={currentWarehouse?.id ?? ''}
            actor={user?.email ?? 'unknown'}
            onSubmitted={() => navigate('/planner/forecast/review')}
          />
        )}
        {method === 'file' && <FileMethod />}
        {method === 'api' && <ApiMethod />}
      </div>
    </div>
  );
}

function CutoffStrip({ vw }: { vw: number }) {
  const items: { type: EventType; label: string; tone: string }[] = [
    { type: 'NORMAL', label: 'Ngày thường', tone: 'DRAFT' },
    { type: 'PAYDAY', label: 'Payday', tone: 'WARNING' },
    { type: 'MID_MONTH', label: 'Giữa tháng', tone: 'WARNING' },
    { type: 'MEGA_SALE', label: 'Mega sale', tone: 'CRITICAL' },
  ];

  return (
    <div
      style={{
        display: 'grid',
        gap: 12,
        gridTemplateColumns: vw < 720 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      }}
    >
      {items.map((i) => (
        <Card key={i.type}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--bx-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: 700,
                }}
              >
                Cut-off
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--bx-text-dark)',
                  fontWeight: 600,
                  marginTop: 2,
                }}
              >
                {i.label}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: 'var(--bx-text-heading)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {CUTOFF_DAYS_BY_EVENT[i.type]} ngày
              </div>
              <Badge size="sm" kind={i.tone}>
                {i.label}
              </Badge>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

interface MatrixCell {
  customerId: string;
  date: string;
  qty: number;
}

function MatrixForm({
  customers,
  calendar,
  warehouseId,
  actor,
  onSubmitted,
}: {
  customers: { id: string; name: string; code: string; tier: string }[];
  calendar: DayCalendarEntry[];
  warehouseId: string;
  actor: string;
  onSubmitted: () => void;
}) {
  const today = toISODate(new Date());
  // Default: next 7 days
  const dates = useMemo(() => {
    const out: string[] = [];
    const base = new Date(today);
    for (let i = 1; i <= 7; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      out.push(toISODate(d));
    }
    return out;
  }, [today]);

  const calMap = useMemo(
    () => new Map(calendar.map((c) => [c.date, c])),
    [calendar],
  );

  const [cells, setCells] = useState<Map<string, number>>(new Map());
  const [submitting, setSubmitting] = useState(false);

  const filled: MatrixCell[] = Array.from(cells.entries())
    .filter(([, v]) => v > 0)
    .map(([k, v]) => {
      const [customerId, date] = k.split('|');
      return { customerId: customerId!, date: date!, qty: v };
    });

  const lateCount = filled.filter((c) => {
    const evt = calMap.get(c.date)?.event_type ?? 'NORMAL';
    return isLate(c.date, evt, today);
  }).length;

  async function submit() {
    if (filled.length === 0 || !warehouseId) return;
    setSubmitting(true);
    try {
      await adjustmentService.submitBulk(
        filled.map((c) => ({
          warehouse_id: warehouseId,
          customer_id: c.customerId,
          date: c.date,
          qty: c.qty,
          source: 'MATRIX_FORM',
          note: '',
        })),
        actor,
        'MATRIX_FORM',
      );
      setCells(new Map());
      onSubmitted();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card
      title="Ma trận điều chỉnh"
      subtitle={`${customers.length} khách hàng × ${dates.length} ngày · điền số đơn dự kiến`}
      padding={0}
      actions={
        <>
          <span style={{ fontSize: 12, color: 'var(--bx-text-muted)' }}>
            {filled.length} ô · {lateCount > 0 ? `${lateCount} trễ hạn` : 'không trễ hạn'}
          </span>
          <Button
            variant="primary"
            icon="check"
            disabled={filled.length === 0 || submitting || !warehouseId}
            onClick={submit}
          >
            {submitting ? 'Đang gửi…' : `Gửi ${filled.length} điều chỉnh`}
          </Button>
        </>
      }
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              <th
                style={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  background: 'var(--bx-surface-2)',
                  textAlign: 'left',
                  padding: '10px 12px',
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--bx-text-muted)',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  borderBottom: '1px solid var(--bx-border-muted)',
                  borderRight: '1px solid var(--bx-border-muted)',
                  minWidth: 180,
                }}
              >
                Khách hàng
              </th>
              {dates.map((d) => {
                const cal = calMap.get(d);
                const evt = cal?.event_type ?? 'NORMAL';
                const late = isLate(d, evt, today);
                return (
                  <th
                    key={d}
                    style={{
                      background: 'var(--bx-surface-2)',
                      textAlign: 'center',
                      padding: '8px 10px',
                      fontSize: 10.5,
                      fontWeight: 600,
                      color: 'var(--bx-text-muted)',
                      borderBottom: '1px solid var(--bx-border-muted)',
                      minWidth: 100,
                    }}
                  >
                    <div style={{ fontFamily: 'var(--bx-font-mono)', fontSize: 11.5, color: 'var(--bx-text-dark)' }}>
                      {d.slice(5)}
                    </div>
                    <div
                      style={{
                        fontSize: 9.5,
                        color:
                          evt === 'NORMAL'
                            ? 'var(--bx-text-muted)'
                            : evt === 'MEGA_SALE'
                              ? '#b91c1c'
                              : '#d97706',
                        marginTop: 2,
                        textTransform: 'none',
                        letterSpacing: 0,
                      }}
                    >
                      {evt === 'NORMAL' ? '—' : evt}
                    </div>
                    {late && (
                      <div style={{ marginTop: 3 }}>
                        <Badge size="sm" kind="WARNING">
                          Trễ
                        </Badge>
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {customers.map((c, ci) => (
              <tr
                key={c.id}
                style={{
                  background: ci % 2 === 1 ? 'rgba(245,245,245,0.4)' : '#fff',
                }}
              >
                <td
                  style={{
                    position: 'sticky',
                    left: 0,
                    background: ci % 2 === 1 ? 'rgba(245,245,245,1)' : '#fff',
                    padding: '8px 12px',
                    borderBottom: '1px solid var(--bx-border-muted)',
                    borderRight: '1px solid var(--bx-border-muted)',
                    zIndex: 1,
                  }}
                >
                  <div style={{ fontWeight: 600, color: 'var(--bx-text-dark)', fontSize: 13 }}>
                    {c.name}
                  </div>
                  <div
                    style={{
                      fontSize: 10.5,
                      color: 'var(--bx-text-muted)',
                      fontFamily: 'var(--bx-font-mono)',
                    }}
                  >
                    {c.code}
                  </div>
                </td>
                {dates.map((d) => {
                  const k = `${c.id}|${d}`;
                  const v = cells.get(k) ?? 0;
                  return (
                    <td
                      key={d}
                      style={{
                        padding: '6px 8px',
                        borderBottom: '1px solid var(--bx-border-muted)',
                        textAlign: 'center',
                      }}
                    >
                      <input
                        type="number"
                        min={0}
                        value={v || ''}
                        onChange={(e) => {
                          const num = Math.max(0, Math.floor(Number(e.target.value) || 0));
                          const next = new Map(cells);
                          if (num === 0) next.delete(k);
                          else next.set(k, num);
                          setCells(next);
                        }}
                        placeholder="—"
                        style={{
                          width: 80,
                          height: 30,
                          textAlign: 'right',
                          fontFamily: 'var(--bx-font-mono)',
                          fontVariantNumeric: 'tabular-nums',
                          fontSize: 12.5,
                          border:
                            '1px solid ' +
                            (v > 0 ? 'var(--bx-primary)' : 'var(--bx-border-muted)'),
                          borderRadius: 6,
                          padding: '0 8px',
                          outline: 'none',
                          background: v > 0 ? '#eef2ff' : '#fff',
                          color: 'var(--bx-text-dark)',
                        }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function FileMethod() {
  return (
    <Card title="Upload file XLSX/CSV" subtitle="Format giống import lịch sử (header VN hoặc EN)">
      <div
        style={{
          padding: 40,
          background: 'var(--bx-surface-2)',
          border: '2px dashed var(--bx-border)',
          borderRadius: 12,
          textAlign: 'center',
        }}
      >
        <Icon name="upload" size={32} style={{ color: 'var(--bx-text-muted)', marginBottom: 12 }} />
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
          Tải file điều chỉnh
        </div>
        <div style={{ fontSize: 12, color: 'var(--bx-text-muted)', marginBottom: 16 }}>
          Drag & drop hoặc click để chọn. Endpoint sử dụng cùng parser với /admin/history-import.
        </div>
        <Button variant="primary" icon="upload" disabled>
          Sắp có
        </Button>
        <div style={{ marginTop: 12, fontSize: 11.5, color: 'var(--bx-text-muted)' }}>
          Hiện tại dùng tab "Ma trận" để gửi nhanh.
        </div>
      </div>
    </Card>
  );
}

function ApiMethod() {
  const [token] = useState('boxme_pk_••••••••••demo');
  return (
    <Card title="API endpoint" subtitle="Tích hợp tự động qua POST request">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Endpoint URL">
          <Input
            value="POST https://workforce.boxme.asia/api/v1/adjustments/batch"
            disabled
            icon="link"
          />
        </Field>
        <Field label="Header Authorization">
          <Input value={`Bearer ${token}`} disabled icon="lock" />
        </Field>
        <Field label="Body schema" hint="Mỗi item là 1 điều chỉnh. Tối đa 1000 items/request.">
          <pre
            style={{
              margin: 0,
              padding: 14,
              background: 'var(--bx-surface-2)',
              borderRadius: 10,
              fontSize: 12,
              fontFamily: 'var(--bx-font-mono)',
              color: 'var(--bx-text-dark)',
              lineHeight: 1.5,
              overflow: 'auto',
            }}
          >
{`{
  "items": [
    {
      "warehouse_code": "VN-HN-01",
      "customer_code": "LOREAL-VN",
      "date": "2026-06-15",
      "qty": 850,
      "note": "Promo mid-month"
    }
  ]
}`}
          </pre>
        </Field>
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            padding: 12,
            background: 'var(--bx-warning-bg)',
            border: '1px solid #fde68a',
            borderRadius: 8,
            fontSize: 12,
            color: 'var(--bx-warning-fg)',
          }}
        >
          <Icon name="info" size={13} style={{ marginTop: 2 }} />
          <span>
            Edge function này hiện ở giai đoạn stub — sẽ deploy thật ở M3.5. Trong mock mode anh
            dùng tab "Ma trận" để demo flow.
          </span>
        </div>
      </div>
    </Card>
  );
}
